'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { Check, Tv2, Clapperboard, Star, Package } from 'lucide-react';
import type { MediaItem } from '@/lib/types';

interface Props {
  item: MediaItem;
  isSelected: boolean;
  onToggle: () => void;
}

const statusClass: Record<string, string> = {
  continuing: 'status-continuing',
  ended: 'status-ended',
  upcoming: 'status-upcoming',
  available: 'status-available',
  announced: 'status-announced',
  'in cinemas': 'status-in-cinemas',
};

export default function PosterCard({ item, isSelected, onToggle }: Props) {
  const [imgError, setImgError] = useState(false);
  const statusKey = item.status.toLowerCase();
  const badgeClass = statusClass[statusKey] ?? 'status-ended';

  const sourceLabel = item.sourceInstanceName ?? (item.mediaType === 'series' ? 'Sonarr' : item.mediaType === 'movie' ? 'Radarr' : 'Custom');

  return (
    <div
      onClick={onToggle}
      className={`poster-card group relative select-none ${isSelected ? 'poster-card-selected' : ''}`}
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(); } }}
    >
      {/* Poster image */}
      <div className="relative aspect-[2/3] overflow-hidden bg-muted">
        {!imgError && item.posterUrl ? (
          <AppImage
            src={item.posterUrl}
            alt={`${item.title} poster`}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 20vw, 160px"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted">
            {item.mediaType === 'series' ? (
              <Tv2 size={28} className="text-muted-foreground" />
            ) : item.mediaType === 'movie' ? (
              <Clapperboard size={28} className="text-muted-foreground" />
            ) : (
              <Package size={28} className="text-muted-foreground" />
            )}
            <span className="text-2xs text-muted-foreground text-center px-2 leading-tight">
              {item.title}
            </span>
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

        {/* Source badge */}
        <div className="absolute top-2 left-2">
          <span
            className={`text-2xs font-semibold px-1.5 py-0.5 rounded-md flex items-center gap-1 ${
              item.mediaType === 'series' ? 'tag-sonarr' : item.mediaType === 'movie' ? 'tag-radarr' : 'bg-muted text-muted-foreground'
            }`}
          >
            {item.mediaType === 'series' ? (
              <Tv2 size={9} />
            ) : item.mediaType === 'movie' ? (
              <Clapperboard size={9} />
            ) : (
              <Package size={9} />
            )}
            {sourceLabel}
          </span>
        </div>

        {/* Selection checkbox */}
        <div
          className={`absolute top-2 right-2 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-150 ${
            isSelected
              ? 'bg-primary border-primary' : 'border-white/60 bg-black/30 opacity-0 group-hover:opacity-100'
          }`}
        >
          {isSelected && <Check size={13} className="text-primary-foreground" strokeWidth={3} />}
        </div>

        {/* Rating */}
        {item.rating > 0 && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Star size={10} className="text-accent fill-accent" />
            <span className="text-2xs font-semibold text-white font-mono-data">{item.rating.toFixed(1)}</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5">
        <p className="text-xs font-semibold text-foreground leading-tight line-clamp-2 mb-1">
          {item.title}
        </p>
        <div className="flex items-center justify-between gap-1">
          <span className="text-2xs text-muted-foreground font-mono-data">{item.year}</span>
          <span className={`text-2xs font-medium px-1.5 py-0.5 rounded-md ${badgeClass}`}>
            {item.status}
          </span>
        </div>
        {item.mediaType === 'series' && item.seasonCount !== undefined && (
          <p className="text-2xs text-muted-foreground mt-1">
            {item.seasonCount} season{item.seasonCount !== 1 ? 's' : ''}
          </p>
        )}
        {item.mediaType === 'movie' && item.runtime !== undefined && (
          <p className="text-2xs text-muted-foreground mt-1 font-mono-data">{item.runtime} min</p>
        )}
        {item.mediaType === 'custom' && (
          <p className="text-2xs text-muted-foreground mt-1">Custom item</p>
        )}
      </div>
    </div>
  );
}